import { Component, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

export interface Crumb {
  label: string,
  link: string
}

// Same names of the menus, e.g. "reits" -> "REITs"
const LABELS: Record<string, string> = {
  home: 'Home',
  account: 'Account',
  'sign-in': 'Sign-In',
  'sign-up': 'Sign-Up',
  settings: 'Settings',
  investments: 'Investments',
  overview: 'Overview',
  companies: 'Companies',
  shares: 'Shares',
  stocks: 'Stocks',
  cryptos: 'Cryptos',
  reits: 'REITs',
  dividends: 'Dividends',
  'fixed-incomes': 'Fixed-incomes',
  'statement-reader': 'Statement reader',
  finances: 'Finances',
  incomes: 'Incomes',
  bills: 'Bills',
  expenses: 'Expenses',
  'credit-card': 'Credit Card',
  categories: 'Categories',
  powerplant: 'Powerplant'
};

// Path of the current page, e.g. "Investments / Overview", built from the URL
@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [RouterLink],
  template: `
    @if (crumbs().length > 1) {
      <nav class="breadcrumb" aria-label="Caminho">
        @for (crumb of crumbs(); track crumb.link; let last = $last) {
          @if (last) {
            <span aria-current="page">{{ crumb.label }}</span>
          } @else {
            <a [routerLink]="crumb.link">{{ crumb.label }}</a>
            <span class="separator" aria-hidden="true">/</span>
          }
        }
      </nav>
    }
  `,
  styles: [`
    .breadcrumb {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      margin-bottom: 8px;
      font-size: 13px;
      color: var(--app-muted);
    }
    a {
      color: var(--app-accent);
      text-decoration: none;
    }
    a:hover {
      text-decoration: underline;
    }
    a:focus-visible {
      outline: 2px solid var(--app-accent);
      outline-offset: 2px;
      border-radius: 2px;
    }
    .separator {
      color: var(--app-faint);
    }
  `]
})
export class BreadcrumbComponent {
  private router = inject(Router);

  crumbs = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null),
      map(() => buildCrumbs(this.router.url))
    ),
    { initialValue: [] as Crumb[] }
  );
}

export function buildCrumbs(url: string): Crumb[] {
  const segments = url.split(/[?#]/)[0].split('/').filter(segment => segment);
  return segments.map((segment, i) => ({
    label: LABELS[segment] ?? toLabel(segment),
    link: '/' + segments.slice(0, i + 1).join('/')
  }));
}

// Unknown segments: "some-page" -> "Some page"
function toLabel(segment: string): string {
  const text = decodeURIComponent(segment).replace(/-/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}
